import {
  Component,
  EventEmitter,
  Output,
  signal,
  Input,
  OnInit,
  ViewChild,
  ElementRef,
  AfterViewInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FleetService } from '../../services/fleet.service';
import {
  ConfirmationModalComponent,
  ConfirmationModalData,
} from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { NotificationService } from '../../../services/notification.service'; // NotificationService;
import { MatSelectModule } from '@angular/material/select';
import { LicensePlateImportResponse } from '../add-license-plates-modal/add-license-plates-modal.component';
import { FleetPlateInput } from '../../models/fleet.model';
import { US_STATES } from '../../../shared/constants/us-states.constant';
import { parsePlateWithState } from '../../utils/fleet-plate-sheet.util';

export interface LicensePlate {
  id: string;
  license_plate: string;
  state: string | null;
}

export interface LicensePlatesApiResponse {
  status: boolean;
  status_code: number;
  data: LicensePlate[];
}

/** Shared base fields for all delete-plate confirmation modals (single and bulk). Only title and description vary. */
const DELETE_PLATE_CONFIRMATION_BASE: Omit<ConfirmationModalData, 'title' | 'description'> = {
  cancelText: 'Cancel',
  confirmText: 'Delete',
  imageSrc: 'assets/images/empty_screen.png',
  imageAlt: 'Delete confirmation illustration',
  showTimeInfo: false,
  timeInfo: '',
  showCancelButton: true,
  showConfirmButton: true,
  isSuccessModal: false,
};

/**
 * Modal component for viewing, previewing, editing, selecting, and deleting fleet license plates.
 * Provides UI and logic for loading license plates for a fleet, previewing CSV-imported plates, editing individual plates, selecting multiple plates, and confirming/deleting plates (single and bulk).
 * @component
 * @selector app-license-plates-modal
 * @example
 *   <app-license-plates-modal
 *     [fleetId]="sampleFleetId"
 *     [fleetName]="sampleFleetName"
 *     [csvPreviewData]="sampleCsvPreview"
 *     [modalMode]="'edit'"
 *     (close)="onCloseModal()"
 *     (importLicensePlates)="onImport()"
 *     (editLicensePlate)="onEditPlate($event)"
 *     (deleteLicensePlate)="onDeletePlate($event)"
 *   ></app-license-plates-modal>
 * @input {string} fleetId - The ID of the fleet whose license plates should be loaded. If omitted and csvPreviewData is provided, the component will show the preview data instead.
 * @input {string} fleetName - The human-readable name of the fleet (used for context/display).
 * @input {string} locationName - The fleet's branch/location label shown in the subtitle (e.g. "The Wash - Visalia, CA").
 * @input {{fleetName: string, licensePlates: string[]} | null} csvPreviewData - Optional CSV preview payload containing a fleetName and a list of license plates; when present the component will display preview items instead of loading from the API.
 * @input {'preview' | 'view' | 'edit'} modalMode - Mode of the modal which controls available interactions and UI (preview, view, or edit). Defaults to 'view'.
 * @output {EventEmitter<void>} close - Emitted when the modal should be closed (user action such as close button).
 * @output {EventEmitter<void>} importLicensePlates - Emitted when the user requests to import license plates (e.g. open CSV import flow).
 * @output {EventEmitter<{id: string, newValue: string}>} editLicensePlate - Emitted after a license plate has been successfully edited; provides the plate id and the new value.
 * @output {EventEmitter<string>} deleteLicensePlate - Emitted after a license plate (or each plate in a bulk operation) has been successfully deleted; payload is the deleted plate id.
 *
 * Key state and reactive signals:
 * - licensePlates: list of LicensePlate items shown in the modal.
 * - loading / error: loading and error state for initial data fetch.
 * - editingPlateId / editingValue / updatingPlate / updateError: edit mode state for inline editing.
 * - newPlatesInput / addingPlates / addPlatesError: state for the comma-separated "Add license plate" field.
 * - isSelectionMode / selectedPlates: multi-select state for bulk actions.
 * - showConfirmationModal / confirmationModalData / plateToDelete / deletingPlates / deleteError: confirmation and deletion state for single and bulk deletes.
 *
 * Key public methods:
 * - loadLicensePlates(): Fetches license plates from the FleetService for the provided fleetId and updates local state.
 * - onClose(): Emits the close event to parent.
 * - onImportLicensePlates(): Emits importLicensePlates to parent to trigger import flow.
 * - onMarkComplete(licensePlateId): Placeholder for mark-complete behavior for a plate.
 * - onAddPlatesInputChange(event) / onAddPlates(): Parse the comma-separated add-plate input and add the resulting plates via the shared bulk-import endpoint.
 *
 * Edit mode methods:
 * - startEdit(plate): Enters inline edit mode for a given plate, focuses the input and prepares state.
 * - cancelEdit(): Cancels edit mode and clears edit state.
 * - saveEdit(): Validates and sends an update to FleetService for the current editing plate, updates local data, shows success/error messages, and emits editLicensePlate on success.
 *
 * Per-plate actions:
 * - onEnterSelectionMode(): Turns on multi-select mode for bulk deletion.
 * - onEdit(plateId): Starts edit mode for the given plate.
 * - onDelete(plateId): Opens the confirmation modal for deleting the given plate.
 *
 * Input handling:
 * - onInputChange(event): Tracks changes to the inline edit input and updates editingValue.
 *
 * Selection and bulk actions:
 * - togglePlateSelection(plateId): Toggles selection state for a plate.
 * - isPlateSelected(plateId): Returns whether a plate is currently selected.
 * - selectAllPlates(): Selects all visible plates.
 * - deselectAllPlates(): Clears selection.
 * - exitSelectionMode(): Exits selection mode and clears selected plates and errors.
 * - getSelectedPlatesCount(): Returns the number of selected plates.
 * - onBulkAction(action): Initiates a bulk edit or delete flow; delete opens a bulk confirmation modal.
 *
 * Confirmation and deletion:
 * - showBulkDeleteConfirmation(plateIds): Prepares and shows confirmation modal for bulk deletion (private helper).
 * - deleteSelectedPlates(plateIds): Calls FleetService to delete multiple plates, updates local state and emits deleteLicensePlate for each deleted id (private helper).
 * - performDelete(plateId): Deletes a single plate via FleetService, updates local state, emits deleteLicensePlate and shows notifications (private helper).
 * - onConfirmationCancel(): Cancels the confirmation modal and clears pending deletion state.
 * - onConfirmationConfirm(): Handles confirmation acceptance; routes to bulk or single delete logic based on stored plateToDelete.
 *
 * Lifecycle hooks:
 * - ngOnInit(): Initializes the component data. If csvPreviewData is provided it will populate licensePlates with preview items; otherwise if fleetId is present it will call loadLicensePlates().
 * - ngAfterViewInit(): Focuses the inline edit input if editing mode is active after view init.
 *
 * Notes:
 * - The component uses a FleetService for API operations and a NotificationService for toast notifications.
 * - All network operations set and clear loading/error signals and handle both success and error responses to keep UI consistent.
 */
@Component({
  selector: 'app-license-plates-modal',
  standalone: true,
  imports: [CommonModule, ConfirmationModalComponent, MatSelectModule],
  templateUrl: './license-plates-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./license-plates-modal.component.scss'],
})
export class LicensePlatesModalComponent implements OnInit, AfterViewInit {
  @Input() fleetId = '';
  @Input() fleetName = '';
  @Input() locationName = '';
  @Input() csvPreviewData: { fleetName: string; licensePlates: FleetPlateInput[] } | null = null;
  @Input() modalMode: 'preview' | 'view' | 'edit' = 'view'; // New input for modal mode
  @Output() close = new EventEmitter<void>();
  @Output() importLicensePlates = new EventEmitter<void>();
  @Output() editLicensePlate = new EventEmitter<{
    id: string;
    newValue: string;
    state: string | null;
  }>();

  /** US states for the inline-edit State select (same list as the vehicle forms). */
  readonly states = US_STATES;
  @Output() deleteLicensePlate = new EventEmitter<string>();

  private readonly messageService = inject(NotificationService);

  // License plates data
  readonly licensePlates = signal<LicensePlate[]>([]);
  readonly loading = signal<boolean>(false);
  readonly error = signal<string | null>(null);
  readonly editingPlateId = signal<string | null>(null);
  readonly editingValue = signal<string>('');
  /** State for the plate being edited; '' means no state. */
  readonly editingState = signal<string>('');
  readonly updatingPlate = signal<boolean>(false);
  readonly updateError = signal<string | null>(null);
  readonly deletingPlates = signal<boolean>(false);
  readonly deleteError = signal<string | null>(null);

  // Add license plate(s) input state
  readonly newPlatesInput = signal<string>('');
  readonly addingPlates = signal<boolean>(false);
  readonly addPlatesError = signal<string | null>(null);

  // Selection mode state
  readonly isSelectionMode = signal<boolean>(false);
  readonly selectedPlates = signal<Set<string>>(new Set());

  // Confirmation modal state
  readonly showConfirmationModal = signal<boolean>(false);
  readonly confirmationModalData = signal<ConfirmationModalData>({
    title: 'Delete License Plate?',
    description:
      "Are you sure you want to delete license plate? This action is permanent and can't be undone.",
    ...DELETE_PLATE_CONFIRMATION_BASE,
  });
  readonly plateToDelete = signal<string | null>(null);

  @ViewChild('editInput', { static: false }) editInput!: ElementRef<HTMLInputElement>;

  constructor(private fleetService: FleetService) {}

  /**
   * Initialize the component's license plates state on init. If csvPreviewData is provided, populate the licensePlates store with preview entries constructed from csvPreviewData.licensePlates; otherwise, if a fleetId exists, call loadLicensePlates() to fetch plates.
   * @example
   * gOnInit()
   * void
   * @returns {void} Performs state updates or triggers loading; does not return a value.
   */
  ngOnInit(): void {
    if (this.csvPreviewData) {
      // Show CSV preview data
      this.licensePlates.set(
        this.csvPreviewData.licensePlates.map((plate, index) => ({
          id: `preview-${index}`,
          license_plate: plate.license_plate,
          state: plate.state,
        }))
      );
    } else if (this.fleetId) {
      this.loadLicensePlates();
    }
  }

  ngAfterViewInit(): void {
    // Focus the edit input when it becomes available
    if (this.editingPlateId() && this.editInput) {
      setTimeout(() => {
        this.editInput.nativeElement.focus();
        this.editInput.nativeElement.select();
      }, 0);
    }
  }

  /**
   * Load license plates for the current fleet and update component state.
   * @example
   * oadLicensePlates()
   * undefined
   * @returns {void} Does not return a value; updates loading, error, and licensePlates stores based on the API response.
   */
  loadLicensePlates(): void {
    this.loading.set(true);
    this.error.set(null);

    this.fleetService.getLicensePlates(this.fleetId).subscribe({
      next: (response: LicensePlatesApiResponse) => {
        if (response.status && response.data) {
          this.licensePlates.set(response.data);
        } else {
          this.error.set('Failed to load license plates');
        }
        this.loading.set(false);
      },
      error: error => {
        console.error('Error loading license plates:', error);
        this.error.set('An error occurred while loading license plates');
        this.loading.set(false);
      },
    });
  }

  onClose(): void {
    this.close.emit();
  }

  onImportLicensePlates(): void {
    this.importLicensePlates.emit();
  }

  onMarkComplete(licensePlateId: string): void {
    // TODO: Implement mark complete functionality
    console.log('Mark complete clicked for:', licensePlateId);
  }

  onAddPlatesInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.newPlatesInput.set(target.value);
  }

  /**
   * Parse the comma-separated "Add license plate" input and add the resulting plates via
   * the same bulk-import endpoint the CSV/XLSX import flow already uses.
   */
  onAddPlates(): void {
    const entries = this.newPlatesInput()
      .split(',')
      .map(plate => plate.trim())
      .filter(plate => plate.length > 0)
      .map(parsePlateWithState);
    const plates = Array.from(
      new Map(entries.map(entry => [`${entry.license_plate}|${entry.state ?? ''}`, entry])).values()
    );

    if (plates.length === 0) {
      return;
    }

    this.addingPlates.set(true);
    this.addPlatesError.set(null);

    this.fleetService.importLicensePlates(this.fleetId, plates).subscribe({
      next: (response: LicensePlateImportResponse) => {
        if (response.status) {
          const inserted = response.data.insertedPlates.map(plate => ({
            id: plate.id,
            license_plate: plate.license_plate,
            state: plate.state ?? null,
          }));
          this.licensePlates.update(existing => [...existing, ...inserted]);
          this.newPlatesInput.set('');

          this.messageService.add({
            severity: 'success',
            summary: inserted.length === 1 ? 'License plate added!' : 'License plates added!',
            detail:
              inserted.length === 1
                ? 'The license plate has been added successfully!'
                : `${inserted.length} license plates have been added successfully!`,
            life: 3000,
          });
        } else {
          this.addPlatesError.set(response.message || 'Failed to add license plates');
        }
        this.addingPlates.set(false);
      },
      error: error => {
        console.error('Error adding license plates:', error);
        this.addPlatesError.set('An error occurred while adding license plates');
        this.addingPlates.set(false);
      },
    });
  }

  // Edit mode methods
  /**
   * Begin editing a license plate by setting the editing state, clearing previous errors, and focusing the edit input.
   * @example
   * tartEdit({ id: 'plate123', license_plate: 'ABC-123' })
   * undefined
   * @param {{LicensePlate}} {{plate}} - The license plate object to edit; its id is used as the editing key and license_plate populates the input.
   * @returns {{void}} Return description in one line.
   **/
  startEdit(plate: LicensePlate): void {
    this.editingPlateId.set(plate.id);
    this.editingValue.set(plate.license_plate);
    this.editingState.set(plate.state ?? '');
    this.updateError.set(null); // Clear any previous error

    // Focus the input field after the view updates
    setTimeout(() => {
      if (this.editInput) {
        this.editInput.nativeElement.focus();
        this.editInput.nativeElement.select();
      }
    }, 0);
  }

  cancelEdit(): void {
    this.editingPlateId.set(null);
    this.editingValue.set('');
    this.editingState.set('');
    this.updateError.set(null); // Clear any error when canceling
  }

  /**
   * Handle editing of a license plate: validates the editing state, calls the API to update the plate,
   * updates local state, shows success/error notifications, emits an edit event, and manages loading state.
   * @example
   * aveEdit()
   * undefined
   * @param {void} none - No parameters.
   * @returns {void} Performs side effects (API call, state updates, notifications) and returns nothing.
   **/
  saveEdit(): void {
    const plateId = this.editingPlateId();
    const newValue = this.editingValue();
    const newState = this.editingState() || null;

    if (plateId && newValue) {
      this.updatingPlate.set(true);
      this.updateError.set(null);

      // Call the API to update the license plate
      this.fleetService.updateLicensePlate(plateId, newValue, newState).subscribe({
        next: response => {
          if (response.status) {
            // Show the saved (server-normalized) plate and state when returned.
            const saved = response.data as Partial<LicensePlate> | undefined;
            this.licensePlates.update(plates =>
              plates.map(plate =>
                plate.id === plateId
                  ? {
                      ...plate,
                      license_plate: saved?.license_plate ?? newValue,
                      state: saved?.state !== undefined ? saved.state : newState,
                    }
                  : plate
              )
            );

            // Show success toast notification
            this.messageService.add({
              severity: 'success',
              summary: 'License plate updated!',
              detail: 'The license plate has been updated successfully!',
              life: 3000,
            });

            // Emit the edit event to parent component
            this.editLicensePlate.emit({ id: plateId, newValue, state: newState });

            // Exit edit mode
            this.editingPlateId.set(null);
            this.editingValue.set('');
            this.editingState.set('');
          } else {
            this.updateError.set(response.message || 'Failed to update license plate');
          }
          this.updatingPlate.set(false);
        },
        error: error => {
          console.error('Error updating license plate:', error);
          this.updateError.set('An error occurred while updating the license plate');
          this.updatingPlate.set(false);
        },
      });
    }
  }

  onEnterSelectionMode(): void {
    this.isSelectionMode.set(true);
    this.deleteError.set(null); // Clear any previous delete error
  }

  onEdit(plateId: string): void {
    const plate = this.licensePlates().find(p => p.id === plateId);
    if (plate) {
      this.startEdit(plate);
    }
  }

  onDelete(plateId: string): void {
    // Store the plate ID to delete and show confirmation modal
    this.plateToDelete.set(plateId);
    this.showConfirmationModal.set(true);
  }

  // Handle input change for edit mode
  onInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target) {
      this.editingValue.set(target.value);
    }
  }

  // Selection mode methods
  togglePlateSelection(plateId: string): void {
    this.selectedPlates.update(selected => {
      const newSelected = new Set(selected);
      if (newSelected.has(plateId)) {
        newSelected.delete(plateId);
      } else {
        newSelected.add(plateId);
      }
      return newSelected;
    });
  }

  isPlateSelected(plateId: string): boolean {
    return this.selectedPlates().has(plateId);
  }

  selectAllPlates(): void {
    const allPlateIds = this.licensePlates().map(plate => plate.id);
    this.selectedPlates.set(new Set(allPlateIds));
  }

  deselectAllPlates(): void {
    this.selectedPlates.set(new Set());
  }

  exitSelectionMode(): void {
    this.isSelectionMode.set(false);
    this.selectedPlates.set(new Set());
    this.deleteError.set(null); // Clear any delete error when exiting selection mode
  }

  getSelectedPlatesCount(): number {
    return this.selectedPlates().size;
  }

  onBulkAction(action: 'edit' | 'delete'): void {
    const selectedIds = Array.from(this.selectedPlates());
    console.log(`Bulk ${action} for plates:`, selectedIds);

    if (action === 'delete') {
      // Show confirmation modal for bulk delete
      this.showBulkDeleteConfirmation(selectedIds);
    }
  }

  /**
   * Prepare and display a confirmation modal for deleting multiple license plates.
   * @example
   * showBulkDeleteConfirmation(['ABC123','XYZ789'])
   * void
   * @param {{string[]}} plateIds - Array of license plate IDs to delete.
   * @returns {{void}} No return value.
   **/
  private showBulkDeleteConfirmation(plateIds: string[]): void {
    // Update confirmation modal data for bulk delete
    this.confirmationModalData.set({
      title: 'Delete License Plates?',
      description: `Are you sure you want to delete ${plateIds.length} license plate${plateIds.length > 1 ? 's' : ''}? This action is permanent and can't be undone.`,
      ...DELETE_PLATE_CONFIRMATION_BASE,
    });

    // Store the plate IDs to delete and show confirmation modal
    this.plateToDelete.set(plateIds.join(','));
    this.showConfirmationModal.set(true);
  }

  /**
   * Delete selected license plates by IDs, update local state, emit delete events, show a toast notification, and handle errors.
   * @example
   * deleteSelectedPlates(['plateId1', 'plateId2'])
   * undefined
   * @param {{string[]}} {{plateIds}} - Array of license plate IDs to delete.
   * @returns {{void}} No return value.
   **/
  private deleteSelectedPlates(plateIds: string[]): void {
    if (plateIds.length === 0) {
      return;
    }

    this.deletingPlates.set(true);
    this.deleteError.set(null);

    this.fleetService.deleteLicensePlates(plateIds).subscribe({
      next: response => {
        if (response.status) {
          // Remove deleted plates from local data
          this.licensePlates.update(plates => plates.filter(plate => !plateIds.includes(plate.id)));

          // Emit delete events to parent component
          plateIds.forEach(plateId => {
            this.deleteLicensePlate.emit(plateId);
          });

          // Show success toast notification
          const plateCount = plateIds.length;
          this.messageService.add({
            severity: 'success',
            summary: plateCount === 1 ? 'License plate deleted!' : 'License plates deleted!',
            detail:
              plateCount === 1
                ? 'The license plate has been deleted successfully!'
                : `${plateCount} license plates have been deleted successfully!`,
            life: 3000,
          });

          // Clear selection and exit selection mode
          this.selectedPlates.set(new Set());
          this.exitSelectionMode();
        } else {
          this.deleteError.set(response.message || 'Failed to delete license plates');
        }
        this.deletingPlates.set(false);
      },
      error: error => {
        console.error('Error deleting license plates:', error);
        this.deleteError.set('An error occurred while deleting license plates');
        this.deletingPlates.set(false);
      },
    });
  }

  // Confirmation modal event handlers
  onConfirmationCancel(): void {
    this.showConfirmationModal.set(false);
    this.plateToDelete.set(null);
  }

  /**
   * Confirm and execute deletion of one or more license plates, close the confirmation modal, and clear the pending plate-to-delete state.
   * @example
   * nConfirmationConfirm()
   * undefined
   * @returns {void} No return value.
   */
  onConfirmationConfirm(): void {
    const plateId = this.plateToDelete();
    if (plateId) {
      // Check if it's a bulk delete (comma-separated IDs) or single delete
      if (plateId.includes(',')) {
        // Bulk delete
        const plateIds = plateId.split(',');
        this.deleteSelectedPlates(plateIds);
      } else {
        // Single delete
        this.performDelete(plateId);
      }
    }
    this.showConfirmationModal.set(false);
    this.plateToDelete.set(null);
  }

  /**
   * Performs deletion of a license plate by ID, updating local state, emitting an event, and showing notifications.
   * @example
   * performDelete('ABC123')
   * void
   * @param {{string}} {{plateId}} - ID of the license plate to delete.
   * @returns {{void}} Performs the deletion and side effects (updates local license plate list, emits delete event, shows success/error notifications, and manages loading/error state).
   **/
  private performDelete(plateId: string): void {
    this.deletingPlates.set(true);
    this.deleteError.set(null);

    this.fleetService.deleteLicensePlate(plateId).subscribe({
      next: response => {
        if (response.status) {
          // Remove deleted plate from local data
          this.licensePlates.update(plates => plates.filter(plate => plate.id !== plateId));

          // Emit delete event to parent component
          this.deleteLicensePlate.emit(plateId);

          // Show success toast notification
          this.messageService.add({
            severity: 'success',
            summary: 'License plate deleted!',
            detail: 'The license plate has been deleted successfully!',
            life: 3000,
          });
        } else {
          this.deleteError.set(response.message || 'Failed to delete license plate');
        }
        this.deletingPlates.set(false);
      },
      error: error => {
        console.error('Error deleting license plate:', error);
        this.deleteError.set('An error occurred while deleting license plate');
        this.deletingPlates.set(false);
      },
    });
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
